import { FormEvent, useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  MicrophoneIcon,
  XMarkIcon,
  ChevronLeftIcon,
  ArrowPathIcon,
} from "@heroicons/react/24/outline";
import useTranslation from "../../hooks/useTranslation";
import { useRoleplayTurn } from "../../hooks/useRoleplayTurn";
import { useRoleplaySituationStart } from "../../hooks/useRoleplaySituationStart";
import { useRoleplayHelp } from "../../hooks/useRoleplayHelp";
import { useRoleplayContinueSituation } from "../../hooks/useRoleplayContinueSituation";
import { useRoleplaySituationMap } from "../../hooks/useRoleplaySituationMap";
import type { RoleplayContinueResponse } from "../../hooks/useRoleplayContinueSituation";
import { fetchRoleplaySpeech } from "../../hooks/useRoleplaySpeech";
import { getCsrfCookie } from "../../lib/utils";

type Screen = "situation_input" | "confirmation" | "roleplay" | "scenario_map" | "summary";

type RoleplayMessage = {
  role: "assistant" | "user";
  text: string;
  debug?: {
    model: string;
    instructions: string;
    history: Array<{ role: string; content: string }>;
    raw_user_transcript: string;
    raw_response: Record<string, unknown>;
    final_response: string;
  };
};

type AssistantAudioElement = HTMLAudioElement & {
  __playbackId?: string;
};

const SITUATION_PRESETS = [
  { key: "restaurant", label: "roleplay_preset_restaurant" },
  { key: "hotel", label: "roleplay_preset_hotel" },
  { key: "cafe", label: "roleplay_preset_cafe" },
  { key: "train_station", label: "roleplay_preset_train" },
  { key: "taxi", label: "roleplay_preset_taxi" },
  { key: "shopping", label: "roleplay_preset_shopping" },
  { key: "grocery_store", label: "roleplay_preset_grocery" },
  { key: "doctor", label: "roleplay_preset_doctor" },
  { key: "directions", label: "roleplay_preset_directions" },
  { key: "meeting_people", label: "roleplay_preset_meeting" },
];

export const Route = createFileRoute("/app/dialogue")({
  component: Dialogue,
});

function Dialogue() {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [screen, setScreen] = useState<Screen>("situation_input");
  const [sessionId, setSessionId] = useState<number | null>(null);
  const [situationId, setSituationId] = useState<number | null>(null);
  const [situationLabel, setSituationLabel] = useState("");
  const [assistantRoleDe, setAssistantRoleDe] = useState("");
  const [userRoleDe, setUserRoleDe] = useState("");
  const [greetingIt, setGreetingIt] = useState("");
  const [masteryPct, setMasteryPct] = useState(0);
  const [messages, setMessages] = useState<RoleplayMessage[]>([]);
  const [voiceError, setVoiceError] = useState("");
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [requiresAudioGesture, setRequiresAudioGesture] = useState(false);

  const startMutation = useRoleplaySituationStart();
  const turnMutation = useRoleplayTurn();
  const helpMutation = useRoleplayHelp();
  const continueQuery = useRoleplayContinueSituation();
  const mapQuery = useRoleplaySituationMap(situationId);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const pendingAudioRef = useRef<HTMLAudioElement | null>(null);
  const assistantAudioPlaybackCounterRef = useRef(0);

  const stopAssistantAudioPlayback = useCallback((reason: string = "manual") => {
    console.log("[tts] stop requested", {
      reason,
      activePlaybackId: (audioRef.current as AssistantAudioElement | null)?.__playbackId ?? null,
      pendingPlaybackId: (pendingAudioRef.current as AssistantAudioElement | null)?.__playbackId ?? null,
      activeCurrentTime: audioRef.current?.currentTime ?? null,
      activeDuration: audioRef.current?.duration ?? null,
    });
    const activeAudio = audioRef.current;
    if (activeAudio) {
      activeAudio.onended = null;
      activeAudio.onerror = null;
      activeAudio.onpause = null;
      activeAudio.onloadedmetadata = null;
      const activeUrl = activeAudio.src;
      activeAudio.pause();
      activeAudio.src = "";
      activeAudio.load();
      if (activeUrl && activeUrl.startsWith("blob:")) URL.revokeObjectURL(activeUrl);
      audioRef.current = null;
    }

    const pendingAudio = pendingAudioRef.current;
    if (pendingAudio) {
      pendingAudio.onended = null;
      pendingAudio.onerror = null;
      pendingAudio.onpause = null;
      pendingAudio.onloadedmetadata = null;
      const pendingUrl = pendingAudio.src;
      pendingAudio.pause();
      pendingAudio.src = "";
      if (pendingUrl && pendingUrl.startsWith("blob:")) URL.revokeObjectURL(pendingUrl);
      pendingAudioRef.current = null;
    }

    setRequiresAudioGesture(false);
    setIsSpeaking(false);
  }, []);

  useEffect(() => {
    return () => {
      stopAssistantAudioPlayback("component-unmount");
    };
  }, []);

  const handlePlayPendingAudio = async () => {
    const audio = pendingAudioRef.current;
    if (!audio || !audio.paused) return;
    try {
      console.log("[tts] retry pending playback", {
        playbackId: (audio as AssistantAudioElement).__playbackId ?? null,
      });
      await audio.play();
      setRequiresAudioGesture(false);
    } catch (err) {
      console.error("[tts] pending playback failed", err);
    }
  };

  const handleSituationStart = (description: string, presetKey?: string) => {
    const t0 = performance.now();
    console.log("[timing] situation-start: click → request:", "0ms");
    startMutation.mutate(
      { user_description_de: description, situation_key: presetKey },
      {
        onSuccess: (data) => {
          console.log("[timing] situation-start: request → response:", (performance.now() - t0).toFixed(0) + "ms");
          setSessionId(data.session_id);
          setSituationId(data.situation_id);
          setSituationLabel(data.situation_label_de);
          setAssistantRoleDe(data.assistant_role_de);
          setUserRoleDe(data.user_role_de);
          setGreetingIt(data.greeting_it);
          setMasteryPct(data.mastery_percentage);
          setMessages([{
            role: "assistant",
            text: data.greeting_it,
          }]);
          setScreen("confirmation");
          playAssistantAudio(data.greeting_it);
        },
      },
    );
  };

  const handleContinue = () => {
    if (continueQuery.data) {
      setSessionId(continueQuery.data.session_id);
      setSituationId(continueQuery.data.situation_id);
      setSituationLabel(continueQuery.data.situation_label_de);
      setMasteryPct(continueQuery.data.mastery_percentage);
      const pastTurns: RoleplayMessage[] = (continueQuery.data.turns || []).map(
        (t) => ({ role: t.role as "assistant" | "user", text: t.text, correction: t.correction, branch: t.branch }),
      );
      if (pastTurns.length > 0) {
        setMessages(pastTurns);
        setScreen("roleplay");
      } else {
        setMessages([
          {
            role: "assistant",
            text: continueQuery.data.greeting_it || "Ciao, continuiamo?",
          },
        ]);
        setScreen("roleplay");
        if (continueQuery.data.greeting_it) {
          playAssistantAudio(continueQuery.data.greeting_it);
        }
      }
    }
  };

  const handleConfirmationProceed = () => {
    setScreen("roleplay");
  };

  const handleConfirmationBack = () => {
    stopAssistantAudioPlayback("confirmation-back");
    setScreen("situation_input");
    setMessages([]);
  };

  const playAssistantAudio = async (text: string) => {
    stopAssistantAudioPlayback("replace-playback");
    setVoiceError("");
    setIsSpeaking(true);
    try {
      assistantAudioPlaybackCounterRef.current += 1;
      const playbackId = String(assistantAudioPlaybackCounterRef.current);
      console.log("[tts] request start", {
        playbackId,
        textLength: text.length,
        textPreview: text.slice(0, 120),
      });
      const ttsStart = performance.now();
      const blob = await fetchRoleplaySpeech({ text, language: "ITALIAN", voice: "onyx" });
      console.log("[timing] tts request duration:", (performance.now() - ttsStart).toFixed(0) + "ms");
      console.log("[tts] response received", {
        playbackId,
        blobSize: blob.size,
        blobType: blob.type,
      });
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url) as AssistantAudioElement;
      audio.__playbackId = playbackId;
      audio.preload = "auto";
      (audio as HTMLMediaElement & { playsInline?: boolean }).playsInline = true;
      audioRef.current = audio;
      let unexpectedPauseResumed = false;
      const cleanup = (reason: string) => {
        const isCurrentAudio = audioRef.current === audio;
        const isPendingAudio = pendingAudioRef.current === audio;
        console.log("[tts] cleanup", {
          playbackId,
          reason,
          isCurrentAudio,
          isPendingAudio,
          currentTime: audio.currentTime,
          duration: Number.isFinite(audio.duration) ? audio.duration : null,
        });
        audio.onended = null;
        audio.onerror = null;
        audio.onpause = null;
        audio.onloadedmetadata = null;
        if (isCurrentAudio) audioRef.current = null;
        if (isPendingAudio) pendingAudioRef.current = null;
        audio.src = "";
        URL.revokeObjectURL(url);
      };
      audio.onloadedmetadata = () => {
        console.log("[tts] metadata loaded", {
          playbackId,
          duration: Number.isFinite(audio.duration) ? audio.duration : null,
        });
      };
      audio.onended = () => {
        if (audioRef.current !== audio && pendingAudioRef.current !== audio) {
          cleanup("stale-ended");
          return;
        }
        setIsSpeaking(false);
        console.log("[tts] ended", {
          playbackId,
          currentTime: audio.currentTime,
          duration: Number.isFinite(audio.duration) ? audio.duration : null,
        });
        cleanup("ended");
      };
      audio.onerror = () => {
        if (audioRef.current !== audio && pendingAudioRef.current !== audio) {
          cleanup("stale-error");
          return;
        }
        setIsSpeaking(false);
        console.error("[tts] playback error", {
          playbackId,
          currentTime: audio.currentTime,
          duration: Number.isFinite(audio.duration) ? audio.duration : null,
          networkState: audio.networkState,
          readyState: audio.readyState,
        });
        cleanup("error");
        setVoiceError(translate("speaking_practice_tts_error"));
      };
      audio.onpause = () => {
        const isCurrentAudio = audioRef.current === audio || pendingAudioRef.current === audio;
        const duration = Number.isFinite(audio.duration) ? audio.duration : 0;
        const remaining = duration > 0 ? duration - audio.currentTime : null;
        console.log("[tts] pause", {
          playbackId,
          isCurrentAudio,
          ended: audio.ended,
          currentTime: audio.currentTime,
          duration: duration || null,
          remaining,
        });
        if (
          isCurrentAudio &&
          !audio.ended &&
          remaining !== null &&
          remaining > 0.2 &&
          audio.currentTime > 0.05 &&
          !unexpectedPauseResumed
        ) {
          unexpectedPauseResumed = true;
          console.warn("[tts] attempting one playback resume after unexpected pause", {
            playbackId,
            currentTime: audio.currentTime,
            remaining,
          });
          void audio.play().catch((resumeError) => {
            console.error("[tts] resume after unexpected pause failed", {
              playbackId,
              error: resumeError instanceof Error ? resumeError.message : String(resumeError),
            });
          });
        }
      };
      const playStart = performance.now();
      await audio.play();
      console.log("[timing] audio.play() resolved after:", (performance.now() - playStart).toFixed(0) + "ms");
      console.log("[tts] playback started", {
        playbackId,
        currentTime: audio.currentTime,
      });
    } catch (err: any) {
      if (err?.name === "NotAllowedError" && audioRef.current) {
        pendingAudioRef.current = audioRef.current;
        setRequiresAudioGesture(true);
        console.log("[timing] audio.play() rejected NotAllowedError — pending stored");
        return;
      }
      setIsSpeaking(false);
      setVoiceError(translate("speaking_practice_tts_error"));
    }
  };

  const handleSendTurn = async (userMessage: string, _helpRequested?: string, deTranscript?: string) => {
    if (!sessionId || !userMessage.trim() || turnMutation.isPending) return;
    const msg: RoleplayMessage = { role: "user", text: userMessage };
    setMessages((prev) => [...prev, msg]);
    try {
      const res = await turnMutation.mutateAsync({
        session_id: sessionId,
        user_message: userMessage,
        help_requested: "",
        de_transcript: deTranscript,
      });
      const replyMsg: RoleplayMessage = {
        role: "assistant",
        text: res.reply,
        debug: res.debug,
      };
      setMessages((prev) => [...prev, replyMsg]);
      playAssistantAudio(res.reply);
    } catch {
      const errorMsg: RoleplayMessage = {
        role: "assistant",
        text: translate("roleplay_turn_error"),
      };
      setMessages((prev) => [...prev, errorMsg]);
    }
  };

  const handleHelp = (helpType: string) => {
    if (!sessionId) return;
    helpMutation.mutate(
      { session_id: sessionId, help_type: helpType },
      {
        onSuccess: (data) => {
          const helpMsg: RoleplayMessage = { role: "assistant", text: data.help_response };
          setMessages((prev) => [...prev, helpMsg]);
        },
        onError: () => {
          const errorMsg: RoleplayMessage = { role: "assistant", text: translate("roleplay_turn_error") };
          setMessages((prev) => [...prev, errorMsg]);
        },
      },
    );
  };

  const handleOpenMap = () => {
    if (situationId) setScreen("scenario_map");
  };

  const handleNewSituation = () => {
    stopAssistantAudioPlayback("new-situation");
    setScreen("situation_input");
    setSessionId(null);
    setMessages([]);
  };

  const handleSummaryContinue = () => {
    if (sessionId) setScreen("roleplay");
  };

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/40">
      <div className="relative w-full h-full max-w-lg bg-white flex flex-col shadow-lg overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b border-slate-100 shrink-0">
          <button
            onClick={() => {
              navigate({ to: "/app", search: {} } as never);
            }}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500"
            aria-label={translate("close_button")}
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
          <span className="text-sm font-medium text-slate-600">
            {situationLabel || translate("sidenav_speaking_practice")}
          </span>
          {screen === "roleplay" && masteryPct > 0 && (
            <span className="text-xs text-slate-400">{masteryPct}% {translate("roleplay_mastery")}</span>
          )}
          {screen !== "roleplay" && <div className="w-8" />}
        </header>

        {screen === "situation_input" && (
          <SituationInputView
            translate={translate}
            onStart={handleSituationStart}
            onContinue={handleContinue}
            continueData={continueQuery.data}
            isStarting={startMutation.isPending}
          />
        )}

        {screen === "confirmation" && (
          <ConfirmationView
            translate={translate}
            situationLabel={situationLabel}
            assistantRoleDe={assistantRoleDe}
            userRoleDe={userRoleDe}
            greetingIt={greetingIt}
            masteryPct={masteryPct}
            onProceed={handleConfirmationProceed}
            onBack={handleConfirmationBack}
          />
        )}

        {screen === "roleplay" && (
          <RoleplayView
            translate={translate}
            sessionId={sessionId}
            messages={messages}
            isSpeaking={isSpeaking}
            voiceError={voiceError}
            isGenerating={turnMutation.isPending}
            onSendTurn={handleSendTurn}
            onPrepareListening={() => stopAssistantAudioPlayback("mic-open")}
            onHelp={handleHelp}
            onOpenMap={handleOpenMap}
            onNewSituation={handleNewSituation}
            onEndSession={() => setScreen("summary")}
            onRetryVoice={() => {
              const last = messages.filter((m) => m.role === "assistant").at(-1);
              if (last) playAssistantAudio(last.text);
            }}
            pendingAudioRef={pendingAudioRef}
            requiresAudioGesture={requiresAudioGesture}
            onPlayPendingAudio={handlePlayPendingAudio}
          />
        )}

        {screen === "scenario_map" && (
          <ScenarioMapView
            translate={translate}
            situationLabel={situationLabel}
            masteryPct={masteryPct}
            mapData={mapQuery.data}
            isLoading={mapQuery.isLoading}
            onBack={() => setScreen("roleplay")}
          />
        )}

        {screen === "summary" && (
          <SummaryView
            translate={translate}
            situationLabel={situationLabel}
            messages={messages}
            masteryPct={masteryPct}
            onContinue={handleSummaryContinue}
            onNew={handleNewSituation}
          />
        )}

        {startMutation.isError && (
          <div className="px-4 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100 shrink-0">
            {translate("roleplay_load_error")}
          </div>
        )}
      </div>
    </div>
  );
}

const isMobile = typeof navigator !== "undefined" && /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

let cachedMicId: string | null = null;

async function getMicStream(): Promise<MediaStream> {
  if (isMobile) return navigator.mediaDevices.getUserMedia({ audio: true });
  if (cachedMicId) {
    try {
      return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: cachedMicId } } });
    } catch { cachedMicId = null; }
  }
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
  try {
    const track = stream.getAudioTracks()[0];
    if (track && track.label.toLowerCase().includes("iphone")) {
      stream.getTracks().forEach((t) => t.stop());
      const devices = await navigator.mediaDevices.enumerateDevices();
      const local = devices.find((d) =>
        d.kind === "audioinput" && d.deviceId &&
        d.label && !d.label.toLowerCase().includes("iphone")
      );
      if (local) {
        cachedMicId = local.deviceId;
        return await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: local.deviceId } } });
      }
      return await navigator.mediaDevices.getUserMedia({ audio: true });
    }
  } catch {}
  return stream;
}

async function transcribeAudio(blob: Blob, language = "", context = "", sessionId: number | null = null, mimeType = ""): Promise<{ transcript: string; interpretedTranscript?: string; confidence: number }> {
  const ext = mimeType.includes("webm") ? "webm" : mimeType.includes("mp4") ? "mp4" : "webm";
  const filename = `recording.${ext}`;
  const runAttempt = async (attemptLanguage: string, attemptContext: string) => {
    const formData = new FormData();
    formData.append("audio", blob, filename);
    formData.append("language", attemptLanguage);
    if (attemptContext) formData.append("context", attemptContext);
    if (sessionId) formData.append("session_id", String(sessionId));
    const res = await fetch("/api/reeprise/roleplay/transcribe/", {
      method: "POST",
      headers: { "X-CSRFTOKEN": getCsrfCookie() },
      credentials: "include",
      body: formData,
    });
    if (!res.ok) throw new Error("transcription failed");
    const data = await res.json();
    return {
      transcript: data.transcript || "",
      interpretedTranscript: data.interpreted_transcript || "",
      confidence: data.confidence || 0,
    };
  };

  const primary = await runAttempt(language, context);
  if (primary.transcript.trim()) return primary;
  if (!language && !context) return primary;
  return runAttempt("", "");
}

function SituationInputView({
  translate, onStart, onContinue, continueData, isStarting,
}: {
  translate: (key: string) => string;
  onStart: (description: string, presetKey?: string) => void;
  onContinue: () => void;
  continueData: RoleplayContinueResponse | null | undefined;
  isStarting: boolean;
}) {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState("");
  const [micError, setMicError] = useState("");
  const recognitionRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const manualStopRef = useRef(false);

  const stopListening = () => {
    manualStopRef.current = true;
    try { recognitionRef.current?.stop(); } catch {}
    if (mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.requestData(); } catch {}
      mediaRecorderRef.current.stop();
    }
  };

  const startListening = () => {
    setMicError("");
    setTranscript("");

    const SR = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;

    if (SR) {
      navigator.mediaDevices.getUserMedia({ audio: true }).then((stream) => {
        stream.getTracks().forEach((t) => t.stop());
        manualStopRef.current = false;
        const recognition = new SR();
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.lang = "de-DE";
        let collected = "";
        let silenceTimer: any = null;

        recognition.onresult = (event: any) => {
          for (let i = event.resultIndex || 0; i < event.results.length; i++) {
            if (event.results[i].isFinal) {
              collected += (collected ? " " : "") + event.results[i][0].transcript;
            }
          }
          if (silenceTimer) clearTimeout(silenceTimer);
          silenceTimer = setTimeout(() => {
            if (!manualStopRef.current) recognition.stop();
          }, 2500);
        };
        recognition.onerror = () => {
          if (!manualStopRef.current) setMicError(translate("speaking_practice_mic_error"));
          setIsListening(false);
        };
        recognition.onend = () => {
          if (silenceTimer) clearTimeout(silenceTimer);
          setIsListening(false);
          if (collected.trim()) {
            const t = collected.trim();
            setTranscript(t.charAt(0).toUpperCase() + t.slice(1));
          }
        };
        recognitionRef.current = recognition;
        setIsListening(true);
        recognition.start();
      }).catch((err: any) => {
        setMicError(err?.name === "NotAllowedError"
          ? translate("speaking_practice_mic_denied")
          : translate("speaking_practice_mic_error"));
      });
      return;
    }

    manualStopRef.current = false;
    getMicStream().then((stream) => {
        const mediaRecorder = new MediaRecorder(stream, { mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4", audioBitsPerSecond: 16000 });
        const chunks: BlobPart[] = [];
        mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
        mediaRecorder.onstop = async () => {
          stream.getTracks().forEach((t) => t.stop());
          const blob = new Blob(chunks, { type: mediaRecorder.mimeType });
          if (blob.size === 0) {
            setMicError(translate("speaking_practice_no_speech"));
          } else {
            try {
              const { transcript: text } = await transcribeAudio(blob, "de");
              if (text?.trim()) {
                setTranscript(text.trim().charAt(0).toUpperCase() + text.trim().slice(1));
              } else {
                setMicError(translate("speaking_practice_no_speech"));
              }
            } catch {
              setMicError(translate("speaking_practice_no_speech"));
            }
          }
          setIsListening(false);
        };
        mediaRecorderRef.current = mediaRecorder;
        setIsListening(true);
        mediaRecorder.start();

        try {
          const audioCtx = new AudioContext();
          const analyser = audioCtx.createAnalyser();
          analyser.fftSize = 256;
          const source = audioCtx.createMediaStreamSource(stream);
          source.connect(analyser);
          const data = new Uint8Array(analyser.frequencyBinCount);
          let silenceStart = 0;
          let raf = 0;
          const recStart = Date.now();

          const check = () => {
            if (manualStopRef.current) { audioCtx.close(); return; }
            if (Date.now() - recStart < 3000) { raf = requestAnimationFrame(check); return; }
            analyser.getByteTimeDomainData(data);
            let sum = 0;
            for (let i = 0; i < data.length; i++) { const v = (data[i] - 128) / 128; sum += v * v; }
            const rms = Math.sqrt(sum / data.length);
            if (rms < 0.01) {
              if (silenceStart === 0) silenceStart = Date.now();
              else if (Date.now() - silenceStart > 1500) {
                cancelAnimationFrame(raf);
                audioCtx.close();
                if (mediaRecorderRef.current?.state === "recording") mediaRecorderRef.current.stop();
                return;
              }
            } else { silenceStart = 0; }
            raf = requestAnimationFrame(check);
          };
          check();
        } catch {}
      }).catch((err: any) => {
        setMicError(err?.name === "NotAllowedError"
          ? translate("speaking_practice_mic_denied")
          : translate("speaking_practice_mic_error"));
      });
    };

  const submitVoice = () => { const text = transcript.trim(); if (text) onStart(text); };
  const submitPreset = (key: string) => {
    const preset = SITUATION_PRESETS.find((p) => p.key === key);
    onStart(preset ? translate(preset.label) : key, key);
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 gap-6">
      {isStarting ? (
        <div className="flex flex-col items-center gap-3">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce [animation-delay:0ms]" />
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce [animation-delay:150ms]" />
            <span className="w-2 h-2 rounded-full bg-blue-400 animate-bounce [animation-delay:300ms]" />
          </div>
          <p className="text-sm text-slate-500">{translate("roleplay_situation_creating")}</p>
        </div>
      ) : (
        <>
      <p className="text-lg text-slate-700 text-center font-medium">{translate("roleplay_situation_prompt")}</p>
      {isListening ? (
        <button
          key="topic-mic-stop"
          type="button"
          onClick={stopListening}
          className="w-20 h-20 rounded-full flex items-center justify-center bg-red-500 text-white ring-4 ring-red-100"
          aria-label={translate("speaking_practice_stop_mic")}
          data-testid="topic-mic-stop"
        >
          <MicrophoneIcon className="w-8 h-8" />
        </button>
      ) : (
        <button
          key="topic-mic-start"
          type="button"
          onClick={startListening}
          disabled={isStarting}
          className={`w-20 h-20 rounded-full flex items-center justify-center bg-blue-600 text-white hover:bg-blue-700 ${isStarting ? "opacity-50" : ""}`}
          aria-label={translate("speaking_practice_start_mic")}
          data-testid="topic-mic-start"
        >
          <MicrophoneIcon className="w-8 h-8" />
        </button>
      )}
      {isListening && <p className="text-sm text-slate-500 animate-pulse">{translate("speaking_practice_listening")}</p>}
      {transcript && !isListening && (
        <div className="w-full">
          <p className="text-sm text-slate-600 bg-slate-50 rounded-lg p-3 text-center">{transcript.trim()}</p>
          <button onClick={submitVoice} disabled={isStarting} className="mt-2 w-full py-2 bg-blue-600 text-white rounded-lg font-medium text-sm hover:bg-blue-700 disabled:opacity-50">
            {isStarting ? "..." : translate("roleplay_confirm_start")}
          </button>
          <button onClick={() => setTranscript("")} className="mt-1 w-full py-1.5 text-slate-500 text-sm hover:text-slate-700">
            {translate("roleplay_voice_retry")}
          </button>
        </div>
      )}
      {micError && <p className="text-sm text-red-600 text-center">{micError}</p>}
      <div className="w-full">
        <p className="text-xs text-slate-400 mb-2 text-center">{translate("roleplay_presets_title")}</p>
        <div className="flex flex-wrap justify-center gap-2">
          {SITUATION_PRESETS.map((preset) => (
            <button key={preset.key} onClick={() => submitPreset(preset.key)} disabled={isStarting} className="px-4 py-2 rounded-full border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50">
              {translate(preset.label)}
            </button>
          ))}
        </div>
      </div>
      {continueData && (
        <button onClick={onContinue} className="w-full px-4 py-2.5 rounded-xl bg-slate-50 hover:bg-slate-100 transition text-center">
          <p className="text-xs text-slate-400">{translate("roleplay_continue_last")}</p>
          <p className="text-sm font-medium text-slate-700">{continueData.situation_label_de}</p>
          {continueData.mastery_percentage > 0 && (
            <p className="text-xs text-slate-500">{continueData.mastery_percentage}% {translate("roleplay_mastery")}</p>
          )}
        </button>
      )}
        </>
      )}
    </div>
  );
}

function ConfirmationView({
  translate, situationLabel, assistantRoleDe, userRoleDe, greetingIt, masteryPct, onProceed, onBack,
}: {
  translate: (key: string) => string;
  situationLabel: string; assistantRoleDe: string; userRoleDe: string;
  greetingIt: string; masteryPct: number; onProceed: () => void; onBack: () => void;
}) {
  const onProceedRef = useRef(onProceed);
  onProceedRef.current = onProceed;
  useEffect(() => {
    const timer = setTimeout(() => onProceedRef.current(), 2500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 gap-4">
      <p className="text-lg font-medium text-slate-800">{situationLabel}</p>
      <p className="text-sm text-slate-500">{translate("roleplay_confirm_you_are")} {userRoleDe} · {translate("roleplay_confirm_i_am")} {assistantRoleDe}</p>
      {greetingIt && <p className="text-base text-slate-700 italic mt-2 text-center bg-slate-50 rounded-lg px-4 py-3">{greetingIt}</p>}
      {masteryPct > 0 && <p className="text-xs text-slate-400">{masteryPct}% {translate("roleplay_mastery")}</p>}
      <div className="flex gap-3 mt-4">
        <button onClick={onBack} className="px-4 py-2 rounded-lg border border-slate-200 text-sm text-slate-600 hover:bg-slate-50">{translate("roleplay_confirm_other")}</button>
        <button onClick={onProceed} className="px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">{translate("roleplay_confirm_start")}</button>
      </div>
    </div>
  );
}

function RoleplayView({
  translate, sessionId, messages, isSpeaking, voiceError, isGenerating,
  onSendTurn, onPrepareListening, onHelp, onOpenMap, onNewSituation, onRetryVoice, onEndSession,
  pendingAudioRef, requiresAudioGesture, onPlayPendingAudio,
}: {
  translate: (key: string) => string;
  sessionId: number | null;
  messages: RoleplayMessage[];
  isSpeaking: boolean; voiceError: string; isGenerating: boolean;
  onSendTurn: (text: string, helpRequested?: string, deTranscript?: string) => void;
  onPrepareListening: () => void;
  onHelp: (helpType: string) => void; onOpenMap: () => void; onNewSituation: () => void; onRetryVoice: () => void;
  onEndSession: () => void;
  pendingAudioRef: React.MutableRefObject<HTMLAudioElement | null>;
  requiresAudioGesture: boolean;
  onPlayPendingAudio: () => void;
}) {
  const [isListening, setIsListening] = useState(false);
  const [showTextInput, setShowTextInput] = useState(true);
  const [showHelp, setShowHelp] = useState(false);
  const [textInputValue, setTextInputValue] = useState("");
  const [micError, setMicError] = useState("");
  const [micInfo, setMicInfo] = useState("");
  const [pendingTranscript, setPendingTranscript] = useState("");
  const [transcribing, setTranscribing] = useState(false);
  const [pendingTranscriptEditing, setPendingTranscriptEditing] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recognitionRef = useRef<any>(null);
  const manualStopRef = useRef(false);

  type RecordingContext = {
    id: number;
    chunks: BlobPart[];
    aborted: boolean;
    mimeType: string;
    startTime: number;
    stream: MediaStream;
    audioCtx: AudioContext | null;
    rafId: number;
    timeoutId: ReturnType<typeof setTimeout> | null;
  };
  const recordingCtxRef = useRef<RecordingContext | null>(null);
  const recordingIdCounter = useRef(0);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    return () => {
      abortRecording();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleVisibility = () => {
      if (
        document.visibilityState === "hidden" &&
        mediaRecorderRef.current?.state === "recording"
      ) {
        stopListening("page-hidden");
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  });

  const MIME_CANDIDATES = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4;codecs=mp4a.40.2",
    "audio/mp4",
  ];

  function createRecorder(stream: MediaStream): MediaRecorder {
    const mimeType = MIME_CANDIDATES.find((type) =>
      MediaRecorder.isTypeSupported(type),
    );
    try {
      return mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);
    } catch {
      return new MediaRecorder(stream);
    }
  }

  type StopReason =
    | "manual"
    | "silence"
    | "max-duration"
    | "page-hidden"
    | "track-ended";

  function requestStopRecording(reason: StopReason) {
    const ctx = recordingCtxRef.current;
    if (!ctx || mediaRecorderRef.current?.state !== "recording") return;
    setIsListening(false);
    if (reason === "page-hidden" || reason === "track-ended") {
      ctx.aborted = true;
    } else {
      setTranscribing(true);
    }
    if (reason === "max-duration") {
      setMicInfo(translate("speaking_practice_recording_too_long"));
    }
    if (reason === "track-ended") {
      setMicError(translate("speaking_practice_mic_interrupted"));
    }
    console.debug("[audio] stop requested", {
      id: ctx.id,
      state: mediaRecorderRef.current.state,
      reason,
    });
    try { mediaRecorderRef.current.requestData(); } catch {}
    try { mediaRecorderRef.current.stop(); } catch {}
  }

  function abortRecording() {
    const ctx = recordingCtxRef.current;
    if (ctx) ctx.aborted = true;
    if (mediaRecorderRef.current?.state === "recording") {
      try { mediaRecorderRef.current.stop(); } catch {}
    }
  }

  const stopListening = (reason: StopReason = "manual") => {
    manualStopRef.current = true;
    try { recognitionRef.current?.stop(); } catch {}
    requestStopRecording(reason);
  };

  const processTranscript = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) { setMicError(translate("speaking_practice_mic_error")); return; }
    onSendTurn(trimmed);
  };

  const confirmPendingTranscript = (text: string) => {
    setPendingTranscript("");
    processTranscript(text);
  };

  const startListening = () => {
    onPrepareListening();
    abortRecording();
    setMicError("");
    setMicInfo("");
    setPendingTranscript("");
    setTranscribing(false);
    manualStopRef.current = false;

    recordingIdCounter.current += 1;
    const ctx: RecordingContext = {
      id: recordingIdCounter.current,
      chunks: [],
      aborted: false,
      mimeType: "",
      startTime: Date.now(),
      stream: null as unknown as MediaStream,
      audioCtx: null,
      rafId: 0,
      timeoutId: null,
    };
    recordingCtxRef.current = ctx;

    const onMicError = (err: any) => {
      console.error("Audio recording failed", {
        errorName: err instanceof DOMException ? err.name : undefined,
        errorMessage: err instanceof Error ? err.message : String(err),
        recorderState: mediaRecorderRef.current?.state,
        mimeType: ctx.mimeType,
        visibilityState: document.visibilityState,
      });
      const name = err?.name || "";
      if (name === "NotAllowedError" || name === "AbortError") {
        setMicError(translate("speaking_practice_mic_denied"));
      } else if (name === "NotFoundError") {
        setMicError(translate("speaking_practice_mic_unsupported"));
      } else if (name === "NotReadableError") {
        setMicError(translate("speaking_practice_mic_in_use"));
      } else {
        setMicError(translate("speaking_practice_mic_error"));
      }
      setIsListening(false);
      setTranscribing(false);
    };

    const startMediaRecording = (stream: MediaStream) => {
      ctx.stream = stream;

      const audioTrack = stream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          if (recordingCtxRef.current === ctx && mediaRecorderRef.current?.state === "recording") {
            stopListening("track-ended");
          }
        };
      }

      const mediaRecorder = createRecorder(stream);
      ctx.mimeType = mediaRecorder.mimeType;
      mediaRecorderRef.current = mediaRecorder;

      mediaRecorder.ondataavailable = (e) => {
        if (recordingCtxRef.current !== ctx) return;
        if (e.data.size > 0) {
          console.debug("[audio] dataavailable", { id: ctx.id, size: e.data.size });
          ctx.chunks.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const isStale = recordingCtxRef.current !== ctx;

        cancelAnimationFrame(ctx.rafId);
        if (ctx.timeoutId) clearTimeout(ctx.timeoutId);
        try { ctx.audioCtx?.close(); } catch {}
        ctx.stream.getTracks().forEach((t) => t.stop());

        const blob = new Blob(ctx.chunks, { type: ctx.mimeType });
        const duration = Date.now() - ctx.startTime;

        console.debug("[audio] recording stopped", {
          id: ctx.id,
          chunkCount: ctx.chunks.length,
          blobSize: blob.size,
          mimeType: ctx.mimeType,
          durationMs: duration,
          aborted: ctx.aborted,
          staleRef: isStale,
        });

        if (isStale) return;

        mediaRecorderRef.current = null;
        recordingCtxRef.current = null;
        setIsListening(false);

        if (ctx.aborted) {
          setTranscribing(false);
          return;
        }
        if (blob.size === 0) {
          setMicError(translate("speaking_practice_no_speech"));
          setTranscribing(false);
          return;
        }
        if (duration < 800) {
          setMicError(translate("speaking_practice_recording_too_short"));
          setTranscribing(false);
          return;
        }

        try {
          const lastAiMsg = messages.filter((m) => m.role === "assistant").at(-1)?.text || "";
          const { transcript, interpretedTranscript } = await transcribeAudio(
            blob, "it", lastAiMsg, sessionId, ctx.mimeType,
          );
          const displayTranscript = (interpretedTranscript || transcript || "").trim();
          if (displayTranscript) {
            // A speech service confidence is not permission to submit words the
            // learner has not seen. Mobile users must be able to correct every turn.
            setPendingTranscript(displayTranscript);
            setPendingTranscriptEditing(false);
          } else {
            setMicError(translate("speaking_practice_no_speech"));
          }
        } catch {
          setMicError(translate("speaking_practice_no_speech"));
        } finally {
          setTranscribing(false);
        }
      };

      mediaRecorder.onerror = () => {
        if (recordingCtxRef.current !== ctx) return;
        ctx.aborted = true;
        setMicError(translate("speaking_practice_mic_interrupted"));
        abortRecording();
      };

      mediaRecorder.start(250);
      setIsListening(true);

      try {
        const audioCtx = new AudioContext();
        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 256;
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);
        ctx.audioCtx = audioCtx;
        const data = new Uint8Array(analyser.frequencyBinCount);

        let ambientSamples: number[] = [];
        let calibrated = false;
        let hasDetectedSpeech = false;
        let firstSpeechAt = 0;
        let lastSpeechAt = 0;
        const silenceDurationMs = 1500;
        const calibrationMs = 500;
        const minSpeechCommitMs = 450;

        const check = () => {
          if (recordingCtxRef.current !== ctx) {
            try { audioCtx.close(); } catch {}
            return;
          }
          if (manualStopRef.current) {
            try { audioCtx.close(); } catch {}
            return;
          }
          const now = Date.now();
          const elapsed = now - ctx.startTime;

          analyser.getByteTimeDomainData(data);
          let sum = 0;
          for (let i = 0; i < data.length; i++) {
            const v = (data[i] - 128) / 128;
            sum += v * v;
          }
          const rms = Math.sqrt(sum / data.length);

          if (!calibrated && elapsed < calibrationMs) {
            ambientSamples.push(rms);
            ctx.rafId = requestAnimationFrame(check);
            return;
          }

          if (!calibrated) {
            calibrated = true;
            const quietSamples = [...ambientSamples]
              .sort((a, b) => a - b)
              .slice(0, Math.max(1, Math.ceil(ambientSamples.length * 0.35)));
            const ambientRms = quietSamples.length > 0
              ? quietSamples.reduce((a, b) => a + b, 0) / quietSamples.length
              : 0.004;
            ambientSamples = quietSamples;
            const threshold = Math.min(0.035, Math.max(0.006, ambientRms * 2.2));
            if (rms > threshold) {
              hasDetectedSpeech = true;
              if (firstSpeechAt === 0) firstSpeechAt = now;
              lastSpeechAt = now;
            }
            ctx.rafId = requestAnimationFrame(check);
            return;
          }

          const threshold = Math.min(
            0.035,
            Math.max(0.006, (ambientSamples.length > 0
              ? ambientSamples.reduce((a, b) => a + b, 0) / ambientSamples.length
              : 0.004) * 2.2,
            ),
          );

          if (rms > threshold) {
            hasDetectedSpeech = true;
            if (firstSpeechAt === 0) firstSpeechAt = now;
            lastSpeechAt = now;
          }

          if (
            hasDetectedSpeech &&
            firstSpeechAt > 0 &&
            now - firstSpeechAt >= minSpeechCommitMs &&
            lastSpeechAt > 0 &&
            now - lastSpeechAt >= silenceDurationMs &&
            recordingCtxRef.current === ctx
          ) {
            try { audioCtx.close(); } catch {}
            requestStopRecording("silence");
            return;
          }

          ctx.rafId = requestAnimationFrame(check);
        };
        ctx.rafId = requestAnimationFrame(check);
      } catch {}

      ctx.timeoutId = setTimeout(() => {
        requestStopRecording("max-duration");
      }, 60_000);
    };

    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        if (typeof MediaRecorder === "undefined") {
          stream.getTracks().forEach((t) => t.stop());
          setMicError(translate("speaking_practice_mic_unsupported"));
          setIsListening(false);
          return;
        }
        startMediaRecording(stream);
      })
      .catch(onMicError);
  };

  const lastAssistantText = messages.filter((m) => m.role === "assistant").at(-1)?.text || "";
  const helpOptions = [
    { type: "what_does_this_mean", label: translate("roleplay_help_what_means") },
    { type: "what_should_i_say", label: translate("roleplay_help_what_say") },
    { type: "slower", label: translate("roleplay_help_slower") },
    { type: "give_me_start", label: translate("roleplay_help_give_start") },
    { type: "repeat", label: translate("roleplay_help_repeat") },
    { type: "show_simple_answer", label: translate("roleplay_help_simple") },
  ];

  return (
    <div className="flex-1 flex flex-col min-h-0">
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${msg.role === "user" ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-800"}`}>
              {msg.text}
            </div>
          </div>
        ))}
        {isSpeaking && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl px-4 py-2.5 text-sm text-slate-400">{translate("speaking_practice_speaking")}</div>
          </div>
        )}
        {isGenerating && (
          <div className="flex justify-start">
            <div className="bg-slate-100 rounded-2xl px-4 py-2.5 flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce [animation-delay:0ms]" />
              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce [animation-delay:150ms]" />
              <span className="w-2 h-2 rounded-full bg-slate-300 animate-bounce [animation-delay:300ms]" />
            </div>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>

      {pendingTranscript && (
        <div className="border-t border-slate-100 bg-white px-4 py-3 shrink-0">
          <p className="text-xs text-slate-500 mb-2">{translate("roleplay_transcribe_did_you_mean")}</p>
          {pendingTranscriptEditing ? (
            <form onSubmit={(e: FormEvent) => { e.preventDefault(); const v = (e.currentTarget as HTMLFormElement).transcript_edit.value.trim(); if (v) confirmPendingTranscript(v); }} className="space-y-2">
              <input name="transcript_edit" type="text" defaultValue={pendingTranscript} className="w-full rounded-lg border border-slate-200 px-3 py-2 text-base text-slate-700 focus:outline-none focus:border-blue-400" />
              <div className="flex gap-2">
                <button type="submit" className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">{translate("roleplay_transcribe_send")}</button>
                <button type="button" onClick={() => setPendingTranscriptEditing(false)} className="flex-1 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-slate-50">{translate("roleplay_transcribe_cancel")}</button>
              </div>
            </form>
          ) : (
            <div className="space-y-2">
              <div className="text-base text-slate-700 bg-slate-50 rounded-lg px-3 py-2">{pendingTranscript}</div>
              <div className="flex gap-2">
                <button onClick={() => confirmPendingTranscript(pendingTranscript)} className="flex-1 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">{translate("roleplay_transcribe_send")}</button>
                <button onClick={() => setPendingTranscriptEditing(true)} className="flex-1 py-2 border border-slate-200 text-slate-600 rounded-lg text-sm hover:bg-slate-50">{translate("roleplay_transcribe_edit")}</button>
              </div>
            </div>
          )}
        </div>
      )}

      {voiceError && (
        <div className="px-4 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100 flex items-center justify-between">
          <span>{voiceError}</span>
          <button onClick={onRetryVoice} className="p-1 text-red-600 hover:bg-red-100 rounded"><ArrowPathIcon className="w-4 h-4" /></button>
        </div>
      )}
      {micError && (
        <div className="px-4 py-2 text-sm text-red-600 bg-red-50 border-t border-red-100 text-center">{micError}</div>
      )}

      {showHelp && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-2">
          <p className="text-xs text-slate-500 mb-1.5">{lastAssistantText}</p>
          <div className="grid grid-cols-2 gap-1.5">
            {helpOptions.map((opt) => (
              <button key={opt.type} onClick={() => { onHelp(opt.type); setShowHelp(false); }} className="text-left px-2.5 py-1.5 rounded-lg text-xs text-slate-700 hover:bg-white border border-slate-200">
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {showDebug && (
        <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 max-h-96 overflow-y-auto shrink-0">
          <p className="text-xs font-medium text-slate-500 mb-2">Debug</p>
          {messages.filter((m) => m.debug).map((m, i) => (
            <details key={i} className="mb-2 text-xs">
              <summary className="text-slate-600 cursor-pointer font-mono">
                Turn {i}: {m.role} — {m.text.slice(0, 50)}...
              </summary>
              <div className="mt-1 pl-3 space-y-1 text-slate-500 font-mono whitespace-pre-wrap break-all">
                <p><strong>Model:</strong> {m.debug!.model}</p>
                <details><summary className="cursor-pointer"><strong>Instructions</strong></summary><pre className="text-[10px] mt-0.5">{m.debug!.instructions}</pre></details>
                <details><summary className="cursor-pointer"><strong>History</strong></summary><pre className="text-[10px] mt-0.5">{JSON.stringify(m.debug!.history, null, 2)}</pre></details>
                <p><strong>Raw transcript:</strong> {m.debug!.raw_user_transcript}</p>
                <details><summary className="cursor-pointer"><strong>Raw response</strong></summary><pre className="text-[10px] mt-0.5">{JSON.stringify(m.debug!.raw_response, null, 2)}</pre></details>
                <p><strong>Final response:</strong> {m.debug!.final_response}</p>
              </div>
            </details>
          ))}
        </div>
      )}

      {showTextInput && (
        <form onSubmit={(e: FormEvent) => { e.preventDefault(); const v = textInputValue.trim(); if (!v) return; onSendTurn(v); setTextInputValue(""); }} className="border-t border-slate-100 bg-white px-4 py-2 shrink-0">
          <div className="flex gap-2">
            <input type="text" value={textInputValue} onChange={(e) => setTextInputValue(e.target.value)} placeholder={translate("roleplay_text_placeholder")} className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-base text-slate-700 placeholder-slate-400 focus:outline-none focus:border-blue-400" />
            <button type="submit" className="shrink-0 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">{translate("roleplay_text_send")}</button>
          </div>
        </form>
      )}

      <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0">
        <div className="flex items-center justify-center mb-2">
          <button
            onClick={() => { isListening ? stopListening() : startListening(); }}
            disabled={isGenerating || transcribing}
            className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-200 ${
              transcribing ? "bg-amber-500 text-white animate-pulse ring-4 ring-amber-200"
              : isGenerating ? "bg-slate-300 text-slate-400 cursor-not-allowed ring-0"
              : isListening ? "bg-red-500 text-white ring-4 ring-red-200"
              : "bg-blue-600 text-white hover:bg-blue-700 hover:scale-105 ring-4 ring-blue-200/50"
            }`}
            aria-label={translate(
              transcribing
                ? "roleplay_transcribing"
                : isListening
                  ? "speaking_practice_stop_mic"
                  : "speaking_practice_start_mic",
            )}
            data-testid={
              transcribing
                ? "roleplay-mic-processing"
                : isListening
                  ? "roleplay-mic-stop"
                  : "roleplay-mic-start"
            }
          >
            <MicrophoneIcon className="w-7 h-7" />
          </button>
        </div>
        <p className="text-xs text-center text-slate-400 mb-1">
          {transcribing ? translate("roleplay_transcribing")
           : isListening ? translate("speaking_practice_listening")
           : translate("roleplay_speak_prompt")}
        </p>
        {micInfo && (
          <p className="text-xs text-center text-amber-600 mb-1">{micInfo}</p>
        )}
        {requiresAudioGesture && pendingAudioRef.current?.paused && (
          <div className="flex justify-center mb-2">
            <button
              onClick={onPlayPendingAudio}
              className="px-4 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 hover:bg-amber-200"
            >
              Tap to hear the AI response
            </button>
          </div>
        )}
        <div className="flex items-center justify-center gap-2 mt-2">
          <button onClick={() => { setShowTextInput(!showTextInput); setShowHelp(false); }} className={`px-3 py-1.5 rounded-full text-xs font-medium ${showTextInput ? "bg-blue-100 text-blue-700" : "border border-slate-200 text-slate-500"}`}>
            {translate("roleplay_text_toggle")}
          </button>
          <button onClick={() => { setShowHelp(!showHelp); setShowTextInput(false); }} className={`px-3 py-1.5 rounded-full text-xs font-medium ${showHelp ? "bg-amber-100 text-amber-700" : "border border-slate-200 text-slate-500"}`}>
            {translate("roleplay_help_button")}
          </button>
          <button onClick={() => onHelp("repeat")} className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 text-slate-500">{translate("roleplay_repeat_button")}</button>
          <button onClick={() => onHelp("slower")} className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 text-slate-500">{translate("roleplay_slower_button")}</button>
          <button onClick={onOpenMap} className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 text-slate-500">{translate("roleplay_map_button")}</button>
          <button onClick={onEndSession} className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 text-slate-500 hover:bg-slate-50">Beenden</button>
          <button onClick={onNewSituation} className="px-3 py-1.5 rounded-full text-xs font-medium border border-slate-200 text-slate-500 hover:bg-slate-50">{translate("roleplay_new_situation")}</button>
          <button onClick={() => setShowDebug(!showDebug)} className={`px-3 py-1.5 rounded-full text-xs font-medium ${showDebug ? "bg-slate-700 text-white" : "border border-slate-200 text-slate-400"}`}>Debug</button>
        </div>
      </div>
    </div>
  );
}

function ScenarioMapView({
  translate, situationLabel, masteryPct, mapData, isLoading, onBack,
}: {
  translate: (key: string) => string;
  situationLabel: string; masteryPct: number;
  mapData?: { situation_label_de: string; mastery_percentage: number; branches: Array<{ key: string; label_de: string; status: string; practice_count: number }> } | null;
  isLoading: boolean; onBack: () => void;
}) {
  const statusIcon = (status: string) => {
    switch (status) {
      case "mastered": return <span className="text-green-600 text-sm">✓</span>;
      case "comfortable": return <span className="text-blue-600 text-sm">✓</span>;
      case "practiced_once": return <span className="text-amber-600 text-sm">◐</span>;
      case "needs_practice": return <span className="text-red-500 text-sm">!</span>;
      default: return <span className="text-slate-300 text-sm">□</span>;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-3">
        <ChevronLeftIcon className="w-4 h-4" />{translate("roleplay_map_button")}
      </button>
      <p className="text-lg font-medium text-slate-800">{situationLabel}</p>
      <div className="mt-1 mb-4">
        <div className="w-full bg-slate-100 rounded-full h-2">
          <div className="bg-blue-600 h-2 rounded-full transition-all" style={{ width: `${mapData?.mastery_percentage || masteryPct}%` }} />
        </div>
        <p className="text-xs text-slate-400 mt-1">{mapData?.mastery_percentage || masteryPct}% {translate("roleplay_mastery")}</p>
      </div>
      {isLoading && <p className="text-sm text-slate-400 text-center py-4">{translate("app_loading")}</p>}
      {mapData?.branches && mapData.branches.length > 0 && (
        <div className="space-y-1">
          {mapData.branches.map((branch) => (
            <div key={branch.key} className="flex items-center justify-between px-3 py-2.5 rounded-lg bg-slate-50">
              <div className="flex items-center gap-2">{statusIcon(branch.status)}<span className="text-sm text-slate-700">{branch.label_de}</span></div>
              <span className="text-xs text-slate-400">{branch.practice_count}x</span>
            </div>
          ))}
        </div>
      )}
      {!mapData?.branches?.length && !isLoading && <p className="text-sm text-slate-400 text-center py-4">{translate("roleplay_map_empty")}</p>}
      <button onClick={onBack} className="mt-4 w-full py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700">{translate("roleplay_map_continue")}</button>
    </div>
  );
}

function SummaryView({
  translate, situationLabel, messages, masteryPct, onContinue, onNew,
}: {
  translate: (key: string) => string;
  situationLabel: string; messages: RoleplayMessage[]; masteryPct: number;
  onContinue: () => void; onNew: () => void;
}) {
  const userMessages = messages.filter((m) => m.role === "user");
  const assistantMessages = messages.filter((m) => m.role === "assistant");
  const phrases = assistantMessages.slice(-3).map((m) => m.text);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-6">
      <p className="text-lg font-medium text-slate-800">{situationLabel} {translate("roleplay_summary_title")}</p>
      {masteryPct > 0 && <p className="text-sm text-slate-500 mt-1">{masteryPct}% {translate("roleplay_mastery")}</p>}
      <div className="mt-4 space-y-3">
        <p className="text-sm font-medium text-slate-700">{translate("roleplay_summary_practiced")}</p>
        <p className="text-sm text-slate-500">{userMessages.length} {userMessages.length === 1 ? translate("roleplay_summary_turn_single") : translate("roleplay_summary_turns")} {translate("roleplay_summary_title")}</p>
      </div>
      {phrases.length > 0 && (
        <div className="mt-4">
          <p className="text-sm font-medium text-slate-700 mb-2">{translate("roleplay_summary_phrases")}</p>
          <div className="space-y-1.5">
            {phrases.map((phrase, i) => (
              <p key={i} className="text-sm text-slate-600 bg-slate-50 rounded-lg px-3 py-2 italic">{phrase}</p>
            ))}
          </div>
        </div>
      )}
      <p className="text-sm font-medium text-slate-700 mt-4 mb-2">{translate("roleplay_summary_next")}</p>
      <div className="space-y-2 mt-3">
        <button onClick={onContinue} className="w-full py-3 bg-blue-600 text-white rounded-xl font-medium text-sm hover:bg-blue-700">{situationLabel} {translate("roleplay_summary_continue")}</button>
        <button onClick={onNew} className="w-full py-3 border border-slate-200 text-slate-600 rounded-xl font-medium text-sm hover:bg-slate-50">{translate("roleplay_summary_new")}</button>
      </div>
    </div>
  );
}
